// Combat wire protocol for Nexus Forge (Phase 5 Step 6).
//
// Shape-only module: types + builders. Same precedent as
// shared/market-messages.ts and shared/workshop-messages.ts — the
// Envelope generic is inlined here to avoid a circular import with
// shared/messages.ts (which re-exports the union extension below).
// No DO dispatcher wires these yet; Step 9 registers the useAbility
// handler on ExpeditionDO and Step 9's combat-tick loop emits the
// server→client envelopes.
//
// Durability classification (per CLAUDE.md §"Durable vs ephemeral"):
//   DURABLE: abilityResolved, abilityMissed, damageDealt, damageTaken,
//            playerDied  — one per outcome, never per tick; client
//            cannot re-derive them from a fresh snapshot.
//   EPHEMERAL: mobSpawned, mobDefeated — derivable from the expedition
//              snapshot's mob roster.
// The push-classification layer (Step 9's ExpeditionDO emitter)
// enforces this split. This file defines shape only.

import type { AbilityId } from "./abilities.ts";

// -----------------------------------------------------------------------
// Envelope (inlined — same reason as market-messages.ts)
// -----------------------------------------------------------------------

type Envelope<T extends string, P> = {
  type: T;
  ts: number;
  payload: P;
  // Phase 5 Step 3 durable wire augmentation. Mirrors messages.ts.
  seq?: number;
  eventId?: string;
};

// -----------------------------------------------------------------------
// Shared domain shape
// -----------------------------------------------------------------------

export type Position = { x: number; y: number };

// -----------------------------------------------------------------------
// Client → Server
// -----------------------------------------------------------------------

// Lock-on targeting — server resolves against the ability's range +
// cooldown + gear/skill gates (see meetsAbilityRequirements). Aim
// mechanics are explicitly out of scope for Phase 5.
export type ClientUseAbility = Envelope<
  "useAbility",
  { abilityId: AbilityId; targetId: string }
>;

export type ClientCombatMessage = ClientUseAbility;

// -----------------------------------------------------------------------
// Server → Client
// -----------------------------------------------------------------------

// Broadcast to every expedition member so other clients can animate the
// strike. `hit` is redundant with `damage > 0` for non-buff abilities
// but is load-bearing for defensive-stance (damage=0 on a successful
// cast). `ts` here is the ability-resolve timestamp; the envelope's
// outer `ts` is set by the builder at send time.
export type ServerAbilityResolved = Envelope<
  "abilityResolved",
  {
    abilityId: AbilityId;
    attackerId: string;
    targetId: string;
    damage: number;
    hit: boolean;
    ts: number;
  }
>;

// Attacker-only notification when the roll against balance.combat.hitChance
// misses. Not broadcast to defender/party — misses are a quiet event.
export type ServerAbilityMissed = Envelope<
  "abilityMissed",
  { abilityId: AbilityId; attackerId: string; ts: number }
>;

// Personal envelope to the attacker after a hit commits. `targetHp` is
// the defender's hp AFTER the damage is applied — lets the HUD snap
// the target's health bar without a follow-up snapshot.
export type ServerDamageDealt = Envelope<
  "damageDealt",
  { attackerId: string; targetId: string; damage: number; targetHp: number }
>;

// Personal envelope to the defender after a hit commits. `selfHp` is
// the defender's hp AFTER the damage is applied. Mirrors damageDealt
// so each side only processes its own durable record of the exchange.
export type ServerDamageTaken = Envelope<
  "damageTaken",
  { attackerId: string; targetId: string; damage: number; selfHp: number }
>;

// Death notification. Broadcast to all expedition members so the
// renderer can drop / ragdoll the player; respawn is a follow-up event
// the Step 9 expedition-lifecycle module will emit separately.
export type ServerPlayerDied = Envelope<"playerDied", { playerId: string }>;

// Ephemeral — re-derivable from the expedition snapshot's mob roster.
// Broadcast on mob spawn so clients can materialize the sprite without
// waiting for the next snapshot.
export type ServerMobSpawned = Envelope<
  "mobSpawned",
  { mobId: string; mobType: string; position: Position; hp: number }
>;

// Ephemeral — mob roster in the next snapshot will omit the defeated
// mob, so the snapshot absorb path handles the fallback case.
export type ServerMobDefeated = Envelope<"mobDefeated", { mobId: string }>;

export type ServerCombatMessage =
  | ServerAbilityResolved
  | ServerAbilityMissed
  | ServerDamageDealt
  | ServerDamageTaken
  | ServerPlayerDied
  | ServerMobSpawned
  | ServerMobDefeated;

// -----------------------------------------------------------------------
// Builders — one per message type. Names match the contracts §2 regex
// /^(build|make|create).*Message$/.
// -----------------------------------------------------------------------

export const buildUseAbilityMessage = (
  abilityId: AbilityId,
  targetId: string,
): ClientUseAbility => ({
  type: "useAbility",
  ts: Date.now(),
  payload: { abilityId, targetId },
});

export const buildAbilityResolvedMessage = (
  abilityId: AbilityId,
  attackerId: string,
  targetId: string,
  damage: number,
  hit: boolean,
  resolvedAt: number,
): ServerAbilityResolved => ({
  type: "abilityResolved",
  ts: Date.now(),
  payload: { abilityId, attackerId, targetId, damage, hit, ts: resolvedAt },
});

export const buildAbilityMissedMessage = (
  abilityId: AbilityId,
  attackerId: string,
  resolvedAt: number,
): ServerAbilityMissed => ({
  type: "abilityMissed",
  ts: Date.now(),
  payload: { abilityId, attackerId, ts: resolvedAt },
});

export const buildDamageDealtMessage = (
  attackerId: string,
  targetId: string,
  damage: number,
  targetHp: number,
): ServerDamageDealt => ({
  type: "damageDealt",
  ts: Date.now(),
  payload: { attackerId, targetId, damage, targetHp },
});

export const buildDamageTakenMessage = (
  attackerId: string,
  targetId: string,
  damage: number,
  selfHp: number,
): ServerDamageTaken => ({
  type: "damageTaken",
  ts: Date.now(),
  payload: { attackerId, targetId, damage, selfHp },
});

export const buildPlayerDiedMessage = (playerId: string): ServerPlayerDied => ({
  type: "playerDied",
  ts: Date.now(),
  payload: { playerId },
});

export const buildMobSpawnedMessage = (
  mobId: string,
  mobType: string,
  position: Position,
  hp: number,
): ServerMobSpawned => ({
  type: "mobSpawned",
  ts: Date.now(),
  payload: { mobId, mobType, position, hp },
});

export const buildMobDefeatedMessage = (mobId: string): ServerMobDefeated => ({
  type: "mobDefeated",
  ts: Date.now(),
  payload: { mobId },
});
